import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-US");
const pct = (v) => {
  const n = Number(v);
  if (Number.isFinite(n)) return `${n.toFixed(1)}%`;
  if (typeof v === "string" && v.includes("%")) return v;
  return "0.0%";
};

const AeroTable6 = ({ data = [] }) => {
  const pdfRef = useRef(null);

  const computed = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Years shown on X axis and in table
    const years = data.map((d) => String(d.year));

    const totals = data.map((d) => num(d["All Sexes"]));
    const females = data.map((d) => num(d.Female));
    const males = data.map((d) => num(d.Male));

    const first = {
      total: totals[0] || 0,
      female: females[0] || 0,
      male: males[0] || 0,
    };

    const last = {
      year: years[years.length - 1],
      total: totals[totals.length - 1] || 0,
      female: females[females.length - 1] || 0,
      male: males[males.length - 1] || 0,
      femaleShare: data[data.length - 1]?.["Women Share %"],
      maleShare: data[data.length - 1]?.["Men Share %"],
    };

    const totalGrowth =
      first.total > 0
        ? (((last.total - first.total) / first.total) * 100).toFixed(1)
        : "0.0";
    const femaleGrowth =
      first.female > 0
        ? (((last.female - first.female) / first.female) * 100).toFixed(1)
        : "0.0";
    const maleGrowth =
      first.male > 0
        ? (((last.male - first.male) / first.male) * 100).toFixed(1)
        : "0.0";

    // Peak year (total)
    let peakIdx = 0;
    for (let i = 1; i < totals.length; i++) {
      if (totals[i] > totals[peakIdx]) peakIdx = i;
    }

    // Best YoY growth year (total)
    let bestYoYIdx = 1;
    let bestYoYGrowth = -Infinity;
    for (let i = 1; i < totals.length; i++) {
      const prev = totals[i - 1] || 0;
      const cur = totals[i] || 0;
      const g = prev > 0 ? ((cur - prev) / prev) * 100 : 0;
      if (g > bestYoYGrowth) {
        bestYoYGrowth = g;
        bestYoYIdx = i;
      }
    }

    return {
      years,
      totals,
      females,
      males,
      first,
      last,
      totalGrowth,
      femaleGrowth,
      maleGrowth,
      peakYear: years[peakIdx],
      peakTotal: totals[peakIdx],
      bestGrowthYear: years[bestYoYIdx] || years[0],
      bestYoYGrowth,
    };
  }, [data]);

  const chartData = useMemo(() => {
    if (!computed) return { labels: [], datasets: [] };

    return {
      labels: computed.years,
      datasets: [
        {
          label: "Female",
          data: computed.females,
          borderColor: "#EC4899",
          backgroundColor: "#EC489920",
          tension: 0.35,
          fill: true,
          pointRadius: 2,
        },
        {
          label: "Male",
          data: computed.males,
          borderColor: "#0ea5e9",
          backgroundColor: "#0ea5e920",
          tension: 0.35,
          fill: true,
          pointRadius: 2,
        },
        {
          label: "All Sexes",
          data: computed.totals,
          borderColor: "#ffffbb",
          backgroundColor: "#ffffbb20",
          tension: 0.35,
          fill: true,
          pointRadius: 2,
        },
      ],
    };
  }, [computed]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "📊 Employment by Gender (2018–2024)",
        font: { size: 16, weight: "bold" },
        padding: { top: 8, bottom: 8 },
      },
      legend: {
        display: true,
        position: "top",
        labels: { font: { size: 11 }, boxWidth: 12, padding: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        grid: { display: false },
        ticks: { font: { size: window.innerWidth < 768 ? 10 : 12 } },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Employees",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        ticks: {
          font: { size: window.innerWidth < 768 ? 10 : 12 },
          callback: (v) => fmt(v),
        },
        grid: { display: true },
      },
    },
  };

  const downloadPDF = () => {
    const input = pdfRef.current;
    if (!input) return;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, pageWidth - 10, 35);
      };

      const addFooter = (pageNo) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNo}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final branding footer block
      pdf.line(10, 260, pageWidth - 10, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment-by-gender.pdf");
    });
  };

  if (!data || data.length === 0) return <p>No data available</p>;
  if (!computed) return <p>No data available</p>;

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Gender ({computed.years[0]}–{computed.last.year})
        </h2>
        
        {/* Extra Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Female Growth</p>
            <p className="text-xl font-bold text-pink-800">
              {Number(computed.femaleGrowth) >= 0 ? "+" : ""}
              {computed.femaleGrowth}%
            </p>
          </div>

          <div className="p-4 bg-indigo-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Male Growth</p>
            <p className="text-xl font-bold text-indigo-800">
              {Number(computed.maleGrowth) >= 0 ? "+" : ""}
              {computed.maleGrowth}%
            </p>
          </div>

          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Best Total Growth Year (YoY)
            </p>
            <p className="text-xl font-bold text-purple-800">
              {computed.bestGrowthYear} (
              {computed.bestYoYGrowth >= 0 ? "+" : ""}
              {computed.bestYoYGrowth.toFixed(1)}%)
            </p>
          </div>
        </div>

        {/* Share cards (last year) */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Female Share ({computed.last.year})
            </p>
            <p className="text-xl font-bold text-pink-800">
              {pct(computed.last.femaleShare)}
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Male Share ({computed.last.year})
            </p>
            <p className="text-xl font-bold text-blue-800">
              {pct(computed.last.maleShare)}
            </p>
          </div>
        </div> */}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left text-sm">Year</th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      All Sexes
                    </th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      Female
                    </th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      Male
                    </th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      Female %
                    </th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      Male %
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {data.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`hover:bg-gray-50 ${
                        String(item.year) === "2024"
                          ? "bg-green-100 font-bold"
                          : idx % 2 === 0
                            ? "bg-white"
                            : "bg-gray-50/60"
                      }`}
                    >
                      <td className="p-2 text-left text-sm">{item.year}</td>

                      <td
                        className="p-2 text-sm"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(item["All Sexes"])}
                      </td>

                      <td
                        className="p-2 text-sm"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(item.Female)}
                      </td>

                      <td
                        className="p-2 text-sm"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(item.Male)}
                      </td>

                      <td
                        className="p-2 text-sm"
                        style={{ textAlign: "right" }}
                      >
                        {pct(item["Women Share %"])}
                      </td>

                      <td
                        className="p-2 text-sm"
                        style={{ textAlign: "right" }}
                      >
                        {pct(item["Men Share %"])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally on mobile to view all columns.
            </p> */}
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[520px] w-full h-96 p-4">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable6;
